/**
 * Slide data for the EAKC "Two Weeks" explainer.
 * GENERATED by scripts/build-eakc-twoweeks.mjs from slides.data.json — do not hand-edit.
 * Source: multi-agent research+writing workflow over EAKC-MVP (last 2-3 weeks) +
 * trending Claude/Anthropic updates · 02 June 2026.
 */

export type SlideLayout =
  | "title"
  | "section"
  | "statement"
  | "concept"
  | "quote"
  | "bullets"
  | "split"
  | "diagram"
  | "compare"
  | "metric"
  | "timeline"
  | "closing";

export interface Slide {
  n: number;
  kicker?: string;
  title: string;
  subtitle?: string;
  layout: SlideLayout;
  bullets?: { head: string; sub?: string }[];
  metrics?: { value: string; label: string }[];
  compareRows?: { eakc: string; claude: string }[];
  takeaway?: string;
  mermaid?: string;
  visualNote?: string;
  narration: string;
}

export const SLIDES: Slide[] = [
  {
    "n": 1,
    "kicker": "Cold Open",
    "title": "How My Side Project Started Teaching Itself",
    "layout": "title",
    "subtitle": "One solo builder, one AI tool, and the six big ideas of modern AI - explained in plain English.",
    "narration": "I built a little AI tool to answer questions about my own code. I was not trying to launch anything. But within two weeks it started doing something that genuinely surprised me - it began learning from its own work. And the wild part? These are the exact same ideas the big AI labs are racing to ship right now. By the end of this, you'll understand all of them.",
    "visualNote": "Title fades up over a slow zoom; the word 'Teaching Itself' lands last with a soft glow."
  },
  {
    "n": 2,
    "kicker": "Cold Open",
    "title": "Two Weeks. One Person. No Sleep.",
    "layout": "metric",
    "subtitle": "This was not a team or a company - it was one obsession, measured in saved changes.",
    "metrics": [
      {
        "value": "224",
        "label": "saved changes shipped"
      },
      {
        "value": "~2 weeks",
        "label": "start to here"
      },
      {
        "value": "1",
        "label": "person directing AI agents"
      }
    ],
    "takeaway": "One person plus AI helpers can now move at the pace that used to need a whole team.",
    "narration": "Let me give you a sense of the intensity. Two hundred and twenty-four saved changes to the project - each one a real improvement - in about two weeks. One person. The trick is that I wasn't typing all of it by hand. I was directing a small army of AI coding assistants, like a conductor in front of an orchestra. That's the new speed, and it's why a solo project could grow this fast.",
    "visualNote": "Counter ticks up to 224; the three metrics snap in one by one with a pulse."
  },
  {
    "n": 3,
    "kicker": "Cold Open",
    "title": "It Started Learning On Its Own",
    "layout": "statement",
    "subtitle": "The same leap the big AI labs are chasing - happening inside one person's side project.",
    "narration": "Here's the hook. I built an AI to answer questions about my own code and documents. Simple enough. But within two weeks, it started doing something different. After finishing a task, it would quietly look back at how that task went, write itself a short note about what worked, and use that note next time. It learns from experience. That's exactly what the big AI labs are racing to build. This is that story.",
    "visualNote": "Bold statement holds center; 'learning on its own' underlines itself as the voice lands on it."
  },
  {
    "n": 4,
    "kicker": "Cold Open",
    "title": "Six Ideas You'll Actually Get",
    "layout": "bullets",
    "subtitle": "Forget the hype - these are the real building blocks of today's AI, and you'll understand each one.",
    "bullets": [
      {
        "head": "AI on your own stuff",
        "sub": "Instead of generic answers, AI reads your files and documents and answers from them."
      },
      {
        "head": "AI that takes action",
        "sub": "Not just chatting - it can use tools and actually do tasks for you."
      },
      {
        "head": "AI that learns and gets tested",
        "sub": "It improves from experience, and we run exams to check when it's wrong."
      },
      {
        "head": "Solo builder, same ideas",
        "sub": "Why one person and a giant lab independently landed on the exact same playbook."
      }
    ],
    "takeaway": "By the end you could explain modern AI's core moves to a friend over coffee.",
    "narration": "Here's the map for what's coming. Six big ideas. One: giving an AI your own knowledge so it answers from your files, not the whole internet. Two: AI that takes actions, not just talk. Three: AI that learns from its own work. Four: how we actually test whether AI is right. Five: how AI plugs into your other tools. And six: why a solo developer and a giant lab built the very same things. Concepts, not features.",
    "visualNote": "Four cards cascade in; on 'same ideas' a faint line links the solo-builder card to a lab logo silhouette."
  },
  {
    "n": 5,
    "kicker": "Cold Open",
    "title": "Not A Launch. Not A Pitch.",
    "layout": "statement",
    "subtitle": "I built this in private, just for fun, and it quietly turned into all of this.",
    "narration": "Quick bit of honesty before we dive in. I built this in private, just for fun. My entire public footprint for it is three short posts and one video - this one. So this isn't a product launch and it definitely isn't a sales pitch. It's me opening the box, showing you what's inside, and sharing what I learned along the way. That's the whole deal. No catch.",
    "visualNote": "Text appears like a handwritten note; 'opening the box' triggers a subtle lid-lift animation."
  },
  {
    "n": 6,
    "kicker": "Cold Open",
    "title": "Meet The Private Librarian",
    "layout": "concept",
    "subtitle": "In one sentence: it's a librarian who has read all your documents and code, and never forgets.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "A tool that reads your own files and code, then answers questions about them."
      },
      {
        "head": "Why it matters",
        "sub": "Generic chatbots don't know your stuff; this one answers only from your material."
      },
      {
        "head": "It shows its sources",
        "sub": "Every answer links to the exact document or line it came from, so you can check."
      },
      {
        "head": "Two ways in",
        "sub": "You can chat with it directly, and other AI tools can plug in and use it too."
      }
    ],
    "takeaway": "It's a private librarian for your own knowledge - you talk to it, and other AI can borrow it.",
    "narration": "Let me set the stage with one plain picture. Imagine a private librarian who has read every document and every line of code you own, and never forgets a word. You ask a question, it answers - and crucially, it points you to the exact source, so you can check it wasn't bluffing. You can talk to it directly, and other AI tools can plug in and use it too. That's the thing we're about to take apart.",
    "visualNote": "A bookshelf icon morphs into a chat bubble, then sprouts a small plug to suggest other tools connecting."
  },
  {
    "n": 7,
    "kicker": "AI Needs Your World",
    "layout": "section",
    "title": "AI Is Brilliant - And Clueless About You",
    "subtitle": "It has read the whole internet, but it has never met your work.",
    "narration": "Here is the strange thing about today's AI. It can write poetry, debug code, explain quantum physics. But ask it about your company, your project, your decisions, and it goes blank. It is a genius who has read everything ever published, and absolutely nothing about you. That gap is the whole story of this section.",
    "visualNote": "Title slides up; a glowing globe of text shrinks to reveal one dark, empty folder labeled YOU."
  },
  {
    "n": 8,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "A Genius Stranger To Your World",
    "subtitle": "The model knows the public internet cold, but has never seen anything that is actually yours.",
    "bullets": [
      {
        "head": "Trained on the public web",
        "sub": "Models like ChatGPT and Claude learned from billions of public pages, books, and forums."
      },
      {
        "head": "Never saw your private stuff",
        "sub": "Your code, your internal docs, your team's decisions were never part of that training."
      },
      {
        "head": "Brilliant but uninformed",
        "sub": "It can reason beautifully, yet has zero facts about the one thing you care about: yours."
      },
      {
        "head": "Why this matters",
        "sub": "Most real work is about private context, so a public-only brain is a stranger at your desk."
      }
    ],
    "takeaway": "A model knows the world's knowledge but nothing about your specific corner of it.",
    "narration": "The big labs trained these models on the public internet - billions of pages anyone can read. That is why they sound so smart about general topics. But your private code, your internal documents, the decisions your team made last quarter? None of that was ever in the training. So it is brilliant and completely uninformed at the same time. A genius stranger sitting at your desk.",
    "visualNote": "Left half fills with bright web text; right half stays dark with a single locked private folder."
  },
  {
    "n": 9,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "When It Doesn't Know, It Guesses",
    "subtitle": "Intuition: a confident wrong answer is called a hallucination, and it is why people don't trust AI yet.",
    "bullets": [
      {
        "head": "It fills the gap confidently",
        "sub": "Asked about your world, the model invents a plausible answer rather than admitting it doesn't know."
      },
      {
        "head": "Closed-book vs open-book",
        "sub": "Closed-book exam means bluffing from memory; open-book means reading the source first."
      },
      {
        "head": "This is a hallucination",
        "sub": "A fluent, confident answer that is simply made up - the polish hides the error."
      },
      {
        "head": "The trust problem",
        "sub": "You can't tell a real answer from a bluff, so you can't safely rely on either."
      }
    ],
    "takeaway": "Hallucination is confident bluffing, and it is the number one reason people distrust AI.",
    "narration": "When the model has no real information, it doesn't stay quiet - it guesses, and it guesses with total confidence. Think of a student in a closed-book exam who never read the chapter: they bluff fluently. That confident-but-wrong answer has a name - a hallucination. And because it sounds just as polished as a correct answer, you can't tell them apart. That is the single biggest reason people still don't trust AI.",
    "visualNote": "A confident answer types out smoothly, then a red stamp drops onto it reading HALLUCINATION."
  },
  {
    "n": 10,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "Let It Read First, Then Answer",
    "subtitle": "The fix is almost embarrassingly simple: give the AI your material before it speaks.",
    "bullets": [
      {
        "head": "Open the book first",
        "sub": "Instead of answering from memory, the AI reads your real documents, then responds."
      },
      {
        "head": "Answers from your sources",
        "sub": "It pulls facts from what you gave it, not from a fuzzy memory of the internet."
      },
      {
        "head": "Bluffing has nowhere to hide",
        "sub": "When the real text is in front of it, guessing gets replaced by reading."
      },
      {
        "head": "Same trick the labs use",
        "sub": "This open-book approach powers almost every serious AI product you have heard of."
      }
    ],
    "takeaway": "Give the AI your real material first and the confident guessing largely disappears.",
    "narration": "Here is the fix, and it is almost too simple. Instead of letting the AI answer from memory, you hand it your actual material first - then it answers. It is the difference between a closed-book and an open-book exam. With the real text in front of it, there is no reason to bluff; it just reads and reports. This is the same move the big labs use to make their products trustworthy.",
    "visualNote": "A closed book flips open; the answer text now redraws itself sourced from the open pages."
  },
  {
    "n": 11,
    "kicker": "AI Needs Your World",
    "layout": "diagram",
    "title": "How Open-Book Answering Works",
    "subtitle": "Three plain steps: bring your documents, find the relevant pieces, answer only from those.",
    "mermaid": "flowchart LR\n  A[Your documents] --> B[Find the relevant pieces]\n  B --> C[Answer using only those]",
    "narration": "Let's make it concrete. Step one: you give the AI your documents - your code, your notes, your files. Step two: when you ask a question, it searches through everything and pulls out just the few pieces that actually relate to your question. Step three: it writes its answer using only those pieces. Bring the documents, find the relevant bits, answer from them. That is the entire engine.",
    "visualNote": "Three nodes light up left to right in sequence; relevant pieces highlight as they flow into the answer."
  },
  {
    "n": 12,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "It Shows Its Receipts",
    "subtitle": "Every answer links straight back to the exact source, so you can check instead of trust.",
    "bullets": [
      {
        "head": "Links to the exact source",
        "sub": "Each claim points to the specific file or passage it came from, not a vague gesture."
      },
      {
        "head": "Verify instead of believing",
        "sub": "You click through and confirm with your own eyes rather than trusting blindly."
      },
      {
        "head": "These are called citations",
        "sub": "The same footnote idea a good research paper uses, applied to every AI answer."
      },
      {
        "head": "Catching the bluffs",
        "sub": "If an answer can't point to a real source, that absence is itself a warning sign."
      }
    ],
    "takeaway": "Cited answers let you check the work, turning blind trust into quick verification.",
    "narration": "There's one more piece that makes this trustworthy: the AI shows its receipts. Every answer links straight to the exact source it used - the specific file, the specific passage. So instead of trusting it blindly, you click through and check with your own eyes. These links are called citations, the same idea as footnotes in a research paper. And if an answer can't point to a real source, that's your signal to be suspicious.",
    "visualNote": "An answer appears with small source chips; clicking one expands to reveal the exact highlighted passage."
  },
  {
    "n": 13,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "This Whole Idea Is Grounding",
    "subtitle": "Answering only from real, cited sources has a name - and it's the foundation of serious AI.",
    "bullets": [
      {
        "head": "A name for the recipe",
        "sub": "Grounding means tying every answer to actual evidence instead of the model's memory."
      },
      {
        "head": "Read, then cite, then answer",
        "sub": "It bundles the open-book read and the receipts into one trustworthy habit."
      },
      {
        "head": "Under almost every product",
        "sub": "Search assistants, support bots, coding helpers - the good ones are all grounded."
      },
      {
        "head": "Why it's the foundation",
        "sub": "Without grounding, an AI is just a confident talker; with it, you can finally rely on answers."
      }
    ],
    "takeaway": "Grounding - answering only from real cited sources - is the foundation under serious AI products.",
    "narration": "This whole approach - read your material first, answer only from it, and show the receipts - has a name. It's called grounding. It means every answer is tied to real evidence instead of the model's fuzzy memory. And once you know the word, you start seeing it everywhere: the serious AI products from the big labs are nearly all grounded. Without it, you just have a confident talker. With it, you have something you can actually rely on.",
    "visualNote": "The word GROUNDING anchors center; roots grow down from it into a stack of source documents."
  },
  {
    "n": 14,
    "kicker": "AI Needs Your World",
    "layout": "concept",
    "title": "EAKC: Grounding On Your Code",
    "subtitle": "My solo project applies grounding to your own codebase - ask in plain English, get cited answers.",
    "bullets": [
      {
        "head": "Your code becomes answerable",
        "sub": "It reads your repositories and documents, then answers questions about them in plain English."
      },
      {
        "head": "Every answer is cited",
        "sub": "It links to the exact line or file, so you verify rather than trust the bot."
      },
      {
        "head": "Two ways to use it",
        "sub": "It runs as a chat app and as a connector other AI tools can plug straight into."
      },
      {
        "head": "What comes next",
        "sub": "Answering well is just the start; the real question is whether it can learn and improve."
      }
    ],
    "takeaway": "EAKC grounds AI on your own codebase - but answering is only step one of the story.",
    "narration": "That's exactly what EAKC is: grounding pointed at your own codebase. It reads your code and documents, and when you ask a question in plain English, it answers with sources linked right back to the exact file. You can use it as a chat app, or plug it straight into other AI tools like Claude. But answering well is really just the starting line. The bigger question - the one we turn to next - is whether an AI like this can actually learn and get better over time.",
    "visualNote": "A plain-English question lands on a code repo; the cited answer rises, then an arrow points forward to LEARN."
  },
  {
    "n": 15,
    "layout": "section",
    "kicker": "From Answering To Doing",
    "title": "From Answering To Doing",
    "subtitle": "The moment AI stops talking and starts acting on your behalf.",
    "narration": "So far we've watched AI answer questions and pull facts from your files. But something bigger is happening across the whole industry right now. The AI is learning to take actions, not just words. To open tools, run steps, and finish a job. This is the leap everyone's chasing, and here's what it actually looks like.",
    "visualNote": "Title slides in from left, underline sweeps across as the kicker fades up."
  },
  {
    "n": 16,
    "layout": "concept",
    "kicker": "From Answering To Doing",
    "title": "AI That Does, Not Just Talks",
    "subtitle": "The defining shift of this era is software that takes action for you.",
    "bullets": [
      {
        "head": "What changes",
        "sub": "Yesterday's AI typed answers; today's AI can open tools and complete real tasks."
      },
      {
        "head": "Why it matters",
        "sub": "A finished result beats a paragraph of advice you still have to act on yourself."
      },
      {
        "head": "Everyday example",
        "sub": "Instead of telling you how to draft a report, it researches and writes the report."
      },
      {
        "head": "The watch-out",
        "sub": "Doing means real consequences, so the bar for getting it right rises sharply."
      }
    ],
    "takeaway": "The big shift: AI moves from giving answers to delivering finished work.",
    "narration": "Here's the headline shift. The big AI labs aren't just making chatbots that answer better. They're building AI that does the task end to end. Think of the difference between a friend explaining how to book a trip versus a friend who just books it for you. That's the jump from answering to doing, and it changes everything about what these tools are for.",
    "visualNote": "Two words 'TALK' and 'DO' face off; 'DO' grows and pushes 'TALK' off-screen."
  },
  {
    "n": 17,
    "layout": "concept",
    "kicker": "From Answering To Doing",
    "title": "What An Agent Really Is",
    "subtitle": "Intuition: an agent is AI given a goal plus tools, like a capable intern.",
    "bullets": [
      {
        "head": "Plain definition",
        "sub": "An agent is a model handed a goal and permission to use real tools."
      },
      {
        "head": "The intern analogy",
        "sub": "A chatbot advises from the sidelines; an agent actually opens your apps and acts."
      },
      {
        "head": "Why it matters",
        "sub": "Goal plus tools means it can chase an outcome, not just reply once."
      },
      {
        "head": "Concrete example",
        "sub": "Ask it to gather sources, write a summary, and send it where it belongs."
      }
    ],
    "takeaway": "An agent equals a goal plus tools, turning chat into a worker.",
    "narration": "When people say agent, this is all they mean. Take a smart model, give it a clear goal, and hand it tools it can actually use. Suddenly it's less like a chatbot and more like a capable intern. The chatbot tells you what to do; the intern opens your apps and gets it done. Goal plus tools. That simple recipe is what powers every agent you'll hear about.",
    "visualNote": "Icons for goal and tools slide together into a glowing 'agent' badge."
  },
  {
    "n": 18,
    "layout": "diagram",
    "kicker": "From Answering To Doing",
    "title": "How A Mission Runs",
    "subtitle": "A goal becomes a plan, the plan becomes steps, the steps become a result.",
    "mermaid": "flowchart LR\n  A[You set a goal] --> B[AI makes a plan]\n  B --> C[AI does the steps]\n  C --> D[AI delivers the result]",
    "narration": "Under the hood, an agent doesn't just wing it. It works like a checklist. You hand it a goal. It breaks that goal into a plan. It works through the steps one by one, using its tools as it goes. Then it hands you the finished result. Goal, plan, steps, result. Once you see that loop, every agent demo suddenly makes sense.",
    "visualNote": "Each node lights up in sequence left to right, arrow pulse traveling between them."
  },
  {
    "n": 19,
    "layout": "concept",
    "kicker": "From Answering To Doing",
    "title": "Power Without A Seatbelt",
    "subtitle": "An intern who can send emails and change data needs real supervision.",
    "bullets": [
      {
        "head": "The new risk",
        "sub": "Acting on the world means a mistake now sends, deletes, or changes real things."
      },
      {
        "head": "Why advice was safe",
        "sub": "A wrong answer you can ignore; a wrong action already happened to you."
      },
      {
        "head": "Everyday example",
        "sub": "An eager intern who emails the wrong client can't un-send that message."
      },
      {
        "head": "The honest truth",
        "sub": "Autonomy without guardrails isn't bold, it's genuinely dangerous in production."
      }
    ],
    "takeaway": "The more an agent can do, the more damage an unsupervised mistake causes.",
    "narration": "But here's the catch nobody can skip. An intern who can only talk is harmless. An intern who can send emails and change your data is a different story. A wrong answer you just ignore. A wrong action already happened. It's out the door. That's why turning an agent loose with no supervision isn't impressive, it's reckless. Power needs a seatbelt.",
    "visualNote": "A speeding car icon with no seatbelt; a warning glow pulses around it."
  },
  {
    "n": 20,
    "layout": "concept",
    "kicker": "From Answering To Doing",
    "title": "It Asks Before It Acts",
    "subtitle": "Before anything risky, the AI pauses and waits for your yes or no.",
    "bullets": [
      {
        "head": "How the seatbelt works",
        "sub": "On any sensitive step, the agent stops and asks you to approve or deny."
      },
      {
        "head": "Why it matters",
        "sub": "You keep the speed of automation while staying the final decision-maker."
      },
      {
        "head": "Safe versus sensitive",
        "sub": "Routine reading runs freely; sending or changing things needs a human hand."
      },
      {
        "head": "The big labs agree",
        "sub": "Ask-first checkpoints are now standard wherever agents touch real systems."
      }
    ],
    "takeaway": "The fix is simple: the agent pauses on risk and you hold the wheel.",
    "narration": "So what's the seatbelt? Permission. The agent does the boring work at full speed, but the moment it's about to do something risky, it stops and asks. You approve or you deny. That's it. You get the power of automation without handing over the keys. It's the same pattern the big labs use when their agents touch anything real. Power, with a human in the loop.",
    "visualNote": "An approve/deny prompt slides up; a green check and red cross hover, awaiting a tap."
  },
  {
    "n": 21,
    "layout": "concept",
    "kicker": "From Answering To Doing",
    "title": "Missions In My Project",
    "subtitle": "EAKC can research, draft, and send, but a human signs off first.",
    "bullets": [
      {
        "head": "What a mission is",
        "sub": "A standing goal the AI works on across steps, not a one-off chat reply."
      },
      {
        "head": "A real example",
        "sub": "It researches a topic, drafts a clean summary, and prepares to send it out."
      },
      {
        "head": "The sign-off gate",
        "sub": "Before that summary actually goes anywhere, a person reviews and approves it."
      },
      {
        "head": "Why I built it that way",
        "sub": "Once it earned the power to send, it needed the discipline to ask."
      }
    ],
    "takeaway": "A mission does the heavy lifting; a human approves the moment that matters.",
    "narration": "In my own project, I call these missions. The AI can take a goal like research this topic, then draft a summary, then send it to the right place. It does all the legwork on its own. But that last step, the actual sending, waits for me to sign off. That's the seatbelt in practice. The agent does the work; the human owns the decision that has consequences.",
    "visualNote": "Three-step mission ribbon; the final 'send' node holds with a pulsing approval badge."
  },
  {
    "n": 22,
    "layout": "statement",
    "kicker": "From Answering To Doing",
    "title": "The Real Leap Is Next",
    "subtitle": "Answering and acting are powerful. But what if the AI could improve itself?",
    "narration": "So now we have AI that answers from your files and AI that takes action with your approval. Both are huge. But there's one more frontier, and it's the wildest of all. What happens when the AI starts learning from its own work, getting better at the job each time, without you rewriting it? That's where we're heading next.",
    "visualNote": "Text holds center, then a subtle loop arrow draws itself around it as we transition."
  },
  {
    "n": 23,
    "kicker": "It Learns From Experience",
    "layout": "section",
    "title": "The Big Idea: It Learns From Experience",
    "subtitle": "Most AI starts every conversation as a stranger. This one keeps notes and gets better.",
    "narration": "Here's the shift that matters most. Today's AI is brilliant but forgetful, it meets you fresh every single time. In the next few minutes I'll show you an AI that does something different: it remembers you, and then it goes a step further and teaches itself. After each job, it writes down what it learned and uses that next time.",
    "visualNote": "Title fades up over a soft pulse, like a notebook opening."
  },
  {
    "n": 24,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "The AI That Forgets You",
    "subtitle": "The quiet frustration of every chatbot today: yesterday's conversation never happened.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "Each chat starts from zero, with no memory of what you said before."
      },
      {
        "head": "Why it stings",
        "sub": "You re-explain your style, your rules, your context every single time you return."
      },
      {
        "head": "Everyday example",
        "sub": "You tell ChatGPT how you like emails written, then tomorrow it has no idea."
      },
      {
        "head": "The cost",
        "sub": "Hours of repeated setup, and an assistant that never grows with you."
      }
    ],
    "takeaway": "An assistant that forgets you can never truly become your assistant.",
    "narration": "Let's name the daily annoyance first. You sit down with a chatbot, explain your preferences, your tone, your rules, and it does a great job. Then tomorrow you come back and it's a blank slate. You explain everything again. It's like working with a talented colleague who gets amnesia overnight, every single night. That's the wall we all keep hitting.",
    "visualNote": "Two identical chat bubbles side by side, the second one resetting to blank."
  },
  {
    "n": 25,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "First Fix: It Remembers You",
    "subtitle": "Memory means the AI carries what it knows about you from one conversation to the next.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "A lasting store of your preferences and context that survives between chats."
      },
      {
        "head": "Why it matters",
        "sub": "You set things up once, and the AI keeps them instead of asking again."
      },
      {
        "head": "Everyday example",
        "sub": "Tell it once you prefer short replies, and it stays short tomorrow too."
      },
      {
        "head": "The big labs",
        "sub": "This is exactly the memory feature Claude and ChatGPT have been rolling out."
      }
    ],
    "takeaway": "Memory turns a one-off tool into something that knows you over time.",
    "narration": "The first fix is the obvious one: give it a memory. An AI that holds onto who you are between conversations, so you stop repeating yourself. Say once that you like short answers, and it stays short. The big labs are shipping exactly this for Claude and ChatGPT right now. It's a real upgrade, but honestly, it's only the start of the story.",
    "visualNote": "A notebook icon fills with sticky-notes that persist as chats cycle past."
  },
  {
    "n": 26,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "Then It Writes Its Own Lessons",
    "subtitle": "Intuition: memory is what you tell it; learning is what it figures out and writes down itself.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "The AI authors its own short notes, like in this project we do it this way."
      },
      {
        "head": "Two sides",
        "sub": "Memory: you hand it facts. Learning: it discovers patterns and records them unprompted."
      },
      {
        "head": "Why it matters",
        "sub": "Nobody has to teach every rule by hand; it accumulates know-how on its own."
      },
      {
        "head": "Everyday example",
        "sub": "After a task it notes link to the exact source, then does that automatically next time."
      }
    ],
    "takeaway": "A growing notebook of how you work, written by the AI, not by you.",
    "narration": "Now here's the deeper move. Memory is what you tell it. Learning is what it figures out for itself. After doing real work, this AI writes itself a short note, something like in this project, we do it this way. Over time those notes pile up into a growing notebook of how you work, and crucially, you didn't have to dictate any of it. It noticed.",
    "visualNote": "A blank page fills itself with handwritten lesson lines, one appearing per beat."
  },
  {
    "n": 27,
    "kicker": "It Learns From Experience",
    "layout": "diagram",
    "title": "The Learning Loop",
    "subtitle": "Four steps that feed back into themselves, so every task makes the next one sharper.",
    "mermaid": "flowchart LR\n  A[Do a task] --> B[Notice what worked]\n  B --> C[Save a lesson]\n  C --> D[Use it next time]\n  D --> A",
    "narration": "Here's the whole idea in one picture. The AI does a task. It notices what worked and what didn't. It saves a short lesson from that. Then next time, it pulls that lesson in before starting, and the cycle repeats. Each loop makes the next attempt a little better. That feedback circle, learning from your own output, is the engine behind everything in this section.",
    "visualNote": "Animate the arrows lighting up in sequence, then loop back to the start continuously."
  },
  {
    "n": 28,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "The Key Move: It Reviews Itself",
    "subtitle": "After each task it quietly grades its own work and turns the takeaway into a reusable lesson.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "A self-review step that runs after the job, before the next one begins."
      },
      {
        "head": "Why it matters",
        "sub": "Improvement happens without a human watching or correcting every single time."
      },
      {
        "head": "Everyday example",
        "sub": "Like journaling what went well after a meeting so you repeat it tomorrow."
      },
      {
        "head": "What it produces",
        "sub": "Not a diary entry, but a concrete rule it can apply on the next task."
      }
    ],
    "takeaway": "The magic isn't doing the task; it's reflecting on it afterward and keeping the lesson.",
    "narration": "Here's the part that makes it work. After finishing a task, the AI quietly looks back at its own work, almost like a person journaling what went well and what to do differently. But it doesn't just reflect, it converts that reflection into a concrete, reusable lesson it can apply next time. That self-review step, running automatically after every job, is the real key move.",
    "visualNote": "A mirror motif: the output reflects back and condenses into a single lesson card."
  },
  {
    "n": 29,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "Do It A Thousand Times",
    "subtitle": "Repeat the loop enough and a generic AI quietly becomes an expert in your specific work.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "Compounding: each saved lesson stacks on the last, so know-how accumulates."
      },
      {
        "head": "Why it matters",
        "sub": "Off-the-shelf AI knows everything generally and your work specifically not at all."
      },
      {
        "head": "Everyday example",
        "sub": "A new hire is average week one, indispensable by month six, same curve."
      },
      {
        "head": "The watch-out",
        "sub": "It gets good at your patterns, so the quality of your habits matters too."
      }
    ],
    "takeaway": "A thousand small lessons turn a generalist into a specialist in you.",
    "narration": "Now run that loop a thousand times. Each lesson stacks on the one before it, and slowly the AI stops being generic. It starts to get genuinely good at your work, specifically, the way a new hire is average in week one but indispensable six months in. The flip side: it learns your patterns, so the better your own habits, the better it becomes.",
    "visualNote": "A bar of expertise climbs steadily as small lesson cards drop into a stack."
  },
  {
    "n": 30,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "It Earns Trust Gradually",
    "subtitle": "Safe lessons apply on their own; anything sensitive waits for your explicit yes.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "A two-tier system splitting harmless lessons from ones that need approval."
      },
      {
        "head": "Why it matters",
        "sub": "Autonomy where it's safe, a human checkpoint where the stakes are real."
      },
      {
        "head": "Everyday example",
        "sub": "A new hire files routine work alone but checks before emailing a client."
      },
      {
        "head": "The balance",
        "sub": "Fast on the small stuff, careful on anything that could cause harm."
      }
    ],
    "takeaway": "Freedom on the safe lessons, a human gate on the risky ones.",
    "narration": "But you don't want it learning anything and just running with it. So there are two lanes. Safe, low-stakes lessons apply on their own. Anything sensitive waits for your yes before it sticks. It's exactly how you'd onboard a new hire: let them handle routine work solo, but ask them to check with you before they email a client. It earns trust gradually.",
    "visualNote": "Two lanes split: green lessons flow through, amber lessons pause at a gate."
  },
  {
    "n": 31,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "It Never Learns In The Dark",
    "subtitle": "Every lesson it picks up is visible, and any of them can be undone by you.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "An open, readable list of everything the AI has taught itself so far."
      },
      {
        "head": "Why it matters",
        "sub": "Learning you can't see is learning you can't trust or correct."
      },
      {
        "head": "Everyday example",
        "sub": "Spot a bad lesson it picked up, and remove it with one action."
      },
      {
        "head": "Control stays with you",
        "sub": "Nothing is locked in; reversible by design, not a black box."
      }
    ],
    "takeaway": "If you can see it and undo it, you stay in charge of what it becomes.",
    "narration": "And here's the safeguard that ties it together: it never learns in the dark. You can see every lesson it has picked up, in plain language, and you can undo any of them. If it drew the wrong conclusion, you delete that lesson and it's gone. That visibility is the whole point. Learning you can't inspect is learning you can't trust. Control stays firmly with you.",
    "visualNote": "A list of lessons appears; a cursor hovers and one fades out cleanly on delete."
  },
  {
    "n": 32,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "Tool Versus Teammate",
    "subtitle": "Intuition: a tool does exactly what you say; a teammate gets better at helping you.",
    "bullets": [
      {
        "head": "The two sides",
        "sub": "A hammer never improves; a colleague learns your style and anticipates you."
      },
      {
        "head": "What it is",
        "sub": "Learning is the line that separates a static tool from a growing collaborator."
      },
      {
        "head": "Why it matters",
        "sub": "Teammates compound in value; tools stay exactly as useful as day one."
      },
      {
        "head": "Everyday example",
        "sub": "Your calculator never improves; a good assistant gets sharper every month."
      }
    ],
    "takeaway": "The moment software starts improving at helping you, it stops being just a tool.",
    "narration": "Step back and you'll see this draws a line. A tool does exactly what you tell it, no more, no less, forever. A hammer is the same hammer on day one and day one thousand. A teammate is different: a teammate gets better at helping you the longer you work together. The moment software starts learning your work and improving, it crosses from tool into teammate.",
    "visualNote": "Split frame: a static hammer on the left, a figure climbing a learning curve on the right."
  },
  {
    "n": 33,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "The Big Labs Shipped This Too",
    "subtitle": "Anthropic just gave Claude self-written Skills and memory, the same idea, the same moment.",
    "bullets": [
      {
        "head": "What happened",
        "sub": "Claude gained reusable Skills and persistent memory, lessons it carries forward."
      },
      {
        "head": "Why it's striking",
        "sub": "A frontier lab and a solo project landed on the identical design at once."
      },
      {
        "head": "Same core idea",
        "sub": "Remember context, save reusable know-how, apply it on the next task."
      },
      {
        "head": "What it signals",
        "sub": "Learning from experience isn't a gimmick; it's where the whole field is heading."
      }
    ],
    "takeaway": "When a giant lab and one builder arrive at the same answer, it's not a coincidence.",
    "narration": "Here's the wild part. Everything I just described, Anthropic shipped almost the same thing for Claude: Skills it can reuse, and a real memory. Same core idea, remember context, save reusable know-how, apply it next time, and it landed at the same moment in history. When a frontier lab and a one-person project converge on the identical design, that tells you something about where AI is actually going.",
    "visualNote": "Two timelines slide together and snap into alignment at the same point."
  },
  {
    "n": 34,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "I Didn't Copy Them",
    "subtitle": "I hit the same wall, an AI that forgets, and built the same escape hatch.",
    "bullets": [
      {
        "head": "What happened",
        "sub": "Working solo, I ran into forgetfulness and reached for the obvious fix."
      },
      {
        "head": "Why it matters",
        "sub": "Good solutions get rediscovered because the problem forces the same shape."
      },
      {
        "head": "The convergence",
        "sub": "Same wall, same escape hatch, arrived at independently, not borrowed."
      },
      {
        "head": "The takeaway",
        "sub": "This isn't one company's trick; it's the natural next step for AI."
      }
    ],
    "takeaway": "The same problem pushes everyone toward the same answer, that's the real signal.",
    "narration": "And to be clear, I didn't copy the big labs. I hit the exact same wall they did, an AI that forgets, and I reached for the same escape hatch: let it remember, let it learn. That's the heart of this whole video. When a hard problem forces independent builders to invent the same solution, you're not looking at a fad. You're looking at the direction of the field.",
    "visualNote": "Two paths drawn separately from opposite corners meet at one identical shape."
  },
  {
    "n": 35,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "Honest: This Is Young",
    "subtitle": "The learning loop works today, but some pieces are still rough, and I'll tell you which.",
    "bullets": [
      {
        "head": "What works now",
        "sub": "It reviews tasks, writes lessons, and reuses them with you in control."
      },
      {
        "head": "What's still rough",
        "sub": "Catching its own out-of-date answers is detected, but the auto-fix isn't finished."
      },
      {
        "head": "Why say it",
        "sub": "Honest maturity beats a polished demo that hides the cracks."
      },
      {
        "head": "The direction",
        "sub": "Each rough edge has a clear next step, not a hand-wave."
      }
    ],
    "takeaway": "The loop is real; some edges are unfinished, and I won't pretend otherwise.",
    "narration": "Let me be straight with you: this is young. The core learning loop genuinely works, it reviews, it writes lessons, it reuses them. But some pieces are still rough. For instance, it can now catch itself giving a confident answer that's gone stale, but making that fix fully automatic isn't done yet. I'd rather tell you exactly where the edges are than sell you a perfect demo.",
    "visualNote": "A progress bar: most segments filled solid, the last one striped and labeled in progress."
  },
  {
    "n": 36,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "The Shape Of It So Far",
    "subtitle": "It already writes and files its own lessons across many categories, automatically, with a human veto.",
    "bullets": [
      {
        "head": "Self-authored lessons",
        "sub": "It writes its own notes from real work, no human dictation required."
      },
      {
        "head": "Many categories",
        "sub": "Lessons cover different kinds of know-how, not just one narrow trick."
      },
      {
        "head": "Automatic, with a veto",
        "sub": "Safe ones land on their own; you can override or remove any of them."
      },
      {
        "head": "Built by one person",
        "sub": "This learning system came together in two weeks of solo, AI-directed work."
      }
    ],
    "takeaway": "Self-written, categorized, automatic lessons, with a human always holding the veto.",
    "narration": "So where does it actually stand? Today it can write its own lessons from real work and file them across several different categories, all on its own, with you able to veto any of it. No hand-feeding rules. And the honest headline: one person pulled this together in about two weeks by directing AI coding agents. A small team of one, building a system that teaches itself.",
    "visualNote": "Lesson cards sort themselves into labeled bins; a small veto stamp hovers over the flow."
  },
  {
    "n": 37,
    "kicker": "It Learns From Experience",
    "layout": "concept",
    "title": "Software That Adapts To You",
    "subtitle": "For forty years you adapted to software; now the software starts adapting to you.",
    "bullets": [
      {
        "head": "The old way",
        "sub": "You learned the menus, the shortcuts, the quirks of every program."
      },
      {
        "head": "The flip",
        "sub": "Now the software studies your patterns and bends toward how you work."
      },
      {
        "head": "Why you should care",
        "sub": "Less time fighting tools, more time getting actual work done."
      },
      {
        "head": "The watch-out",
        "sub": "Adaptation needs trust, which is why visible and reversible matters."
      }
    ],
    "takeaway": "The burden of adapting quietly moves from you to the machine.",
    "narration": "Here's why this matters even if you never write a line of code. For forty years, using computers meant you adapting to the software, learning its menus, its quirks, its shortcuts. This flips that. The software starts adapting to you instead, learning how you work and meeting you there. That's a quiet but enormous reversal, and it's exactly why being able to see and undo what it learns is so important.",
    "visualNote": "An arrow labeled adapt reverses direction, now pointing from software toward the person."
  },
  {
    "n": 38,
    "kicker": "It Learns From Experience",
    "layout": "statement",
    "title": "But Is It Actually Right?",
    "subtitle": "If it's learning on its own, how do you know what it learned is correct?",
    "narration": "But here's the question that should be nagging at you. If this thing is learning on its own, teaching itself lessons and applying them, how do you actually know it's right? A confident AI that learned the wrong thing is more dangerous than one that just forgets. So next, let's look at how you keep a self-learning AI honest, and catch it when it's wrong.",
    "visualNote": "The word RIGHT? holds center screen, then a magnifying glass slides in as the section turns."
  },
  {
    "n": 39,
    "kicker": "How Do You Know It Is Right",
    "title": "But How Do You Know It Is Right?",
    "subtitle": "Grounded answers are nice. Trusting them is a separate problem.",
    "layout": "section",
    "narration": "We've built an AI that reads your files and links to its sources. But a citation isn't a guarantee. The hardest question in all of AI isn't getting an answer. It's knowing whether to believe the answer you got. So let's talk about trust, and how you actually earn it.",
    "visualNote": "Section title slides up; a question mark glows then resolves into a checkmark outline."
  },
  {
    "n": 40,
    "kicker": "How Do You Know It Is Right",
    "title": "The Failure That Looks Like Success",
    "subtitle": "The most dangerous wrong answer is the one that looks completely right.",
    "layout": "concept",
    "bullets": [
      {
        "head": "Obvious errors are safe",
        "sub": "A garbled or clearly-wrong answer warns you instantly; you double-check it and move on."
      },
      {
        "head": "Confident errors are not",
        "sub": "A fluent, well-cited answer earns your trust before you've checked whether it's actually true."
      },
      {
        "head": "Citations feel like proof",
        "sub": "Linked sources signal 'I did my homework,' but a link is not the same as being correct."
      },
      {
        "head": "Why it matters",
        "sub": "You act on the polished wrong answer precisely because nothing about it looks wrong."
      }
    ],
    "takeaway": "The scariest AI mistakes wear the costume of a right answer.",
    "narration": "Here's the thing newcomers underestimate. An obviously wrong answer is harmless, because you catch it. The dangerous one is fluent, confident, and even cites its sources, so you believe it without checking. Citations feel like proof, but a link is not a guarantee of truth. The best-dressed answer is the one most likely to fool you.",
    "visualNote": "Two answer cards side by side; the polished one glows green then a red flag quietly appears in its corner."
  },
  {
    "n": 41,
    "kicker": "How Do You Know It Is Right",
    "title": "It Happened To Me",
    "subtitle": "A beautiful, well-cited answer described last month, not today.",
    "layout": "concept",
    "bullets": [
      {
        "head": "What went wrong",
        "sub": "The AI answered confidently using older notes while newer, more accurate information sat right beside them."
      },
      {
        "head": "Out of date, dressed as current",
        "sub": "Nothing flagged it as stale; it read as today's truth because it was so well written."
      },
      {
        "head": "Cited and still wrong",
        "sub": "Every source link worked perfectly, which made the outdated answer even more convincing."
      },
      {
        "head": "Why it's sneaky",
        "sub": "Staleness has no tell; the polish that should reassure you is exactly what hides it."
      }
    ],
    "takeaway": "My AI quoted last month's reality with this month's confidence.",
    "narration": "This isn't hypothetical. My own assistant gave me a gorgeous, fully-cited answer, and it was describing how things stood a month ago, not today. Newer information was right there, but it reached for the older notes and wrote them up beautifully. Out of date, dressed as current. Every link worked. That's what made it so convincing.",
    "visualNote": "A calendar page labeled 'last month' fades up behind a glossy answer card stamped 'today'."
  },
  {
    "n": 42,
    "kicker": "How Do You Know It Is Right",
    "title": "Sounds Right Is Not Is Right",
    "subtitle": "Intuition: polish is a feeling, correctness is a fact, and they come apart.",
    "layout": "concept",
    "bullets": [
      {
        "head": "The two sides",
        "sub": "A smooth-talking guide who's never checked the map versus a quiet one who looked it up."
      },
      {
        "head": "What it is",
        "sub": "Confidence and fluency measure how an answer sounds, not whether it's actually true."
      },
      {
        "head": "Why it matters",
        "sub": "Our brains use polish as a shortcut for trust, and AI is extremely good at polish."
      },
      {
        "head": "The watch-out",
        "sub": "When polish is free and easy, you must stop letting it stand in for accuracy."
      }
    ],
    "takeaway": "Never let how good it sounds decide whether you believe it.",
    "narration": "Here's the intuition. Picture two tour guides. One speaks beautifully but never checked the map. The other is plainer but actually looked it up. Your gut trusts the smooth talker, and that's the trap. Sounds right and is right are two different things, and modern AI is dazzlingly good at sounding right. So polish can't be your evidence.",
    "visualNote": "Split frame: a charming guide on the left, a map-reading guide on the right; the polish meter pegs high for the wrong one."
  },
  {
    "n": 43,
    "kicker": "How Do You Know It Is Right",
    "title": "The Defense Is An Eval",
    "subtitle": "Intuition: a driving test for your AI, not a gut feeling about it.",
    "layout": "concept",
    "bullets": [
      {
        "head": "What an eval is",
        "sub": "A fixed test with known right answers the AI must pass before you trust it."
      },
      {
        "head": "The two sides",
        "sub": "Gut feel says 'seems fine to me'; a driving test says 'prove it under the same rules.'"
      },
      {
        "head": "Why it matters",
        "sub": "It replaces opinion with a repeatable check anyone can run and verify the same way."
      },
      {
        "head": "Same word, same idea",
        "sub": "When the big AI labs say 'evals,' this graded exam is exactly what they mean."
      }
    ],
    "takeaway": "An eval turns 'I think it's fine' into 'it passed the test.'",
    "narration": "So what's the defense? It's called an eval, short for evaluation. Think of a driving test. You don't get the license because the instructor likes your vibe; you get it by passing a fixed set of checks. An eval does the same for AI: a graded exam with known answers it must pass before you trust it. This is the same word the big labs use constantly.",
    "visualNote": "A gut-feeling thought bubble crosses out; a stamped 'PASSED' test card slides in to replace it."
  },
  {
    "n": 44,
    "kicker": "How Do You Know It Is Right",
    "title": "Measure First, Enforce Second",
    "subtitle": "You cannot fix, or even trust, what you have never measured.",
    "layout": "concept",
    "bullets": [
      {
        "head": "Measure first",
        "sub": "Build a test that scores the exact failure you're worried about before changing anything."
      },
      {
        "head": "Enforce second",
        "sub": "Once you can score it, make passing that score a requirement, not a hope."
      },
      {
        "head": "Why order matters",
        "sub": "Enforcing before measuring just locks in whatever you guessed, right or wrong."
      },
      {
        "head": "Everyday version",
        "sub": "You can't lose weight by wishing; you weigh yourself first, then set the target."
      }
    ],
    "takeaway": "Score the problem before you try to fix it, or you're guessing.",
    "narration": "There's a discipline underneath this, and it's almost boring: measure first, enforce second. First you build a way to score the exact thing you're worried about. Only then do you make passing that score a hard requirement. Flip the order and you just lock in a guess. It's the difference between weighing yourself and setting a target, versus hoping the scale is kind.",
    "visualNote": "A ruler appears, then a gate; arrow flows ruler then gate, with a no-entry symbol on the reversed order."
  },
  {
    "n": 45,
    "kicker": "How Do You Know It Is Right",
    "title": "A Tiny Exam For One Bug",
    "subtitle": "I built a small automatic test that catches stale-but-confident answers.",
    "layout": "concept",
    "bullets": [
      {
        "head": "What it checks",
        "sub": "It asks the AI a question whose answer changed recently and confirms it uses the newer truth."
      },
      {
        "head": "Runs on every change",
        "sub": "The test fires automatically whenever I touch the system, so nobody has to remember it."
      },
      {
        "head": "Why that matters",
        "sub": "A bug you fixed once can quietly return; an always-on test slams that door shut."
      },
      {
        "head": "Honest status",
        "sub": "It catches the failure reliably; making the fix fully automatic is still in progress."
      }
    ],
    "takeaway": "One small always-on test makes a fixed bug stay fixed.",
    "narration": "So I built a tiny exam aimed at exactly this: the stale-but-confident answer. It asks something whose answer recently changed and checks the AI uses the new truth, not the old. And it runs automatically on every change, so the bug can't quietly sneak back in. I'll be honest: it catches the failure well; making the repair fully automatic is still a work in progress.",
    "visualNote": "A small exam paper auto-stamps PASS/FAIL each time a gear turns; a 'return blocked' barrier drops."
  },
  {
    "n": 46,
    "kicker": "How Do You Know It Is Right",
    "title": "The Unglamorous Core",
    "subtitle": "Good AI isn't magic; it's tests quietly holding quality in place.",
    "layout": "concept",
    "bullets": [
      {
        "head": "What actually holds quality",
        "sub": "Not a clever prompt, but a wall of repeatable tests that run again and again."
      },
      {
        "head": "Why it's invisible",
        "sub": "Tests only get noticed when they fail; working ones do their job silently in the background."
      },
      {
        "head": "The everyday parallel",
        "sub": "Bridges don't stay up by inspiration; they stay up because of boring, constant inspections."
      },
      {
        "head": "The mindset shift",
        "sub": "Stop asking 'is the model smart' and start asking 'what test proves this still works.'"
      }
    ],
    "takeaway": "Reliable AI is built from boring tests, not brilliant moments.",
    "narration": "Here's the unglamorous truth. The thing that actually makes AI trustworthy isn't a flash of brilliance; it's a wall of boring, repeatable tests holding quality in place. You never notice them when they work, the same way you never think about bridge inspections until one is skipped. The real question stops being 'is it smart' and becomes 'what test proves it still works.'",
    "visualNote": "Glittery 'magic' sparkles dissolve to reveal a steady grid of green test checkmarks underneath."
  },
  {
    "n": 47,
    "kicker": "How Do You Know It Is Right",
    "title": "Why The Labs Obsess Over Evals",
    "subtitle": "Trust in AI comes from measurement, not faith, all the way up.",
    "layout": "concept",
    "bullets": [
      {
        "head": "Same problem, bigger stakes",
        "sub": "The makers of ChatGPT and Claude face the exact fluent-but-wrong risk, at massive scale."
      },
      {
        "head": "Their answer is evals",
        "sub": "Before a new model ships, it's run through huge batteries of graded tests."
      },
      {
        "head": "Why you keep hearing it",
        "sub": "When labs claim a model is 'better,' an eval score is what they actually mean."
      },
      {
        "head": "The convergence",
        "sub": "A solo builder and a frontier lab landed on the same discipline: measure, then trust."
      }
    ],
    "takeaway": "From a solo project to the biggest labs, trust is measured, not assumed.",
    "narration": "This is why you hear the big labs talk about evals constantly. The makers of ChatGPT and Claude face the same fluent-but-wrong trap, just at enormous scale, so before any model ships they run it through batteries of graded tests. When they say a model is better, an eval score is usually what they mean. A solo project and a frontier lab converged on the same idea: trust is earned by measurement.",
    "visualNote": "A small home test scales up into a giant grid of lab evals; both share the same green pass color."
  },
  {
    "n": 48,
    "kicker": "How Do You Know It Is Right",
    "title": "Grounded, Learning, And Tested",
    "subtitle": "Three pillars are in place; now we ask how AI reaches your other tools.",
    "layout": "concept",
    "bullets": [
      {
        "head": "Where we are",
        "sub": "The AI answers from your files, improves itself over time, and proves quality with tests."
      },
      {
        "head": "The missing piece",
        "sub": "So far it mostly reads and answers; real work means taking action in other apps."
      },
      {
        "head": "The next question",
        "sub": "How does an AI safely reach into your email, calendar, code, and the tools you already use?"
      },
      {
        "head": "Why it matters next",
        "sub": "An assistant that can act, not just talk, is where the real leverage begins."
      }
    ],
    "takeaway": "An AI you can trust is ready to start actually doing things.",
    "narration": "So let's take stock. We have an AI that's grounded in your files, that learns from each task, and that proves its quality with tests. That's a foundation you can actually trust. But trust is the setup, not the payoff. The real question comes next: how does an AI safely reach beyond chat and into your other tools, to actually get work done? That's where we're headed.",
    "visualNote": "Three labeled pillars lock into place, then a connector line shoots out toward icons of email, calendar, and code."
  },
  {
    "n": 49,
    "kicker": "Giving AI Tools",
    "layout": "section",
    "title": "Giving AI Hands: Tools",
    "subtitle": "An AI that can only talk is half the picture. The other half is an AI that can act.",
    "narration": "So far we've taught the AI to read your files, to learn from its mistakes, and to test itself. But all of that still happens inside a chat box. Now we get to the part that turns a clever conversation into actual work: giving the AI hands, so it can reach out and use the software you already rely on.",
    "visualNote": "Title slides in from left; a glowing hand icon assembles from particles."
  },
  {
    "n": 50,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "Talk Is Cheap Without Hands",
    "subtitle": "An AI trapped in a chat window can advise you, but it can't do anything for you.",
    "bullets": [
      {
        "head": "What a chatbox can do",
        "sub": "It reads, reasons, and writes text back to you. That is the entire box."
      },
      {
        "head": "Why that limits the value",
        "sub": "It can draft the email but not send it, suggest a meeting but not book it."
      },
      {
        "head": "The real prize",
        "sub": "Let it touch your calendar, your inbox, your database, and it goes from advisor to assistant."
      },
      {
        "head": "Where this is heading",
        "sub": "The big labs now race to connect their models to the apps you actually use."
      }
    ],
    "takeaway": "Value shows up the moment an AI can use your software, not just describe it.",
    "narration": "Think about everything ChatGPT does for you today. It reads, it reasons, it writes a reply. That's the whole box. It can draft the perfect email, but it can't send it. It can plan your week, but it can't book a thing. The real value arrives the moment the AI can reach into your calendar, your inbox, your database and actually do the work, turning an advisor into an assistant.",
    "visualNote": "A chat bubble sits boxed; arrows try to escape to app icons but bounce off a wall."
  },
  {
    "n": 51,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "Tool Use, Explained",
    "subtitle": "Intuition: tool use is the AI clicking the buttons in your apps, the way a person would.",
    "bullets": [
      {
        "head": "A plain definition",
        "sub": "The model calls real functions in real software instead of only producing text."
      },
      {
        "head": "The everyday picture",
        "sub": "Like a smart assistant at your desk pressing send, saving a file, looking something up."
      },
      {
        "head": "Why it matters",
        "sub": "Actions get done correctly because the app does them, not because the AI guessed."
      },
      {
        "head": "Where you've seen it",
        "sub": "When Claude or ChatGPT searches the web or runs code, that is tool use."
      }
    ],
    "takeaway": "Tool use means the AI takes real actions by calling real software functions.",
    "narration": "Here's the intuition. Tool use is just the AI clicking the buttons in your apps, the way a person sitting at your desk would. Instead of only writing text, the model calls a real function in real software. It presses send, it saves the file, it looks up the record. You've already felt this whenever Claude searches the web or runs code for you. That little action is tool use.",
    "visualNote": "A cursor-hand hovers over app buttons, pressing each one as labels light up."
  },
  {
    "n": 52,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "The Wiring Nightmare",
    "subtitle": "Before any standard, every AI had to be custom-wired to every single tool, by hand.",
    "bullets": [
      {
        "head": "What the problem was",
        "sub": "Each AI plus each tool needed its own one-off connection built from scratch."
      },
      {
        "head": "Why it exploded",
        "sub": "Ten AIs and ten tools means a hundred separate wirings nobody can maintain."
      },
      {
        "head": "A concrete example",
        "sub": "Connecting one model to email, calendar, and a database was three custom jobs."
      },
      {
        "head": "The cost of the mess",
        "sub": "It didn't scale, so most tools stayed locked away from most AIs."
      }
    ],
    "takeaway": "Custom-wiring every AI to every tool by hand simply could not scale.",
    "narration": "But giving an AI tools used to be a nightmare. Every AI and every tool needed its own custom connection, built by hand, one at a time. Ten AIs and ten tools? That's a hundred separate wirings to build and maintain. Hooking a single model up to email, calendar, and a database meant three custom jobs. It didn't scale, so most tools just stayed locked away.",
    "visualNote": "A tangle of crisscrossing wires multiplies between AI icons and tool icons until it's chaos."
  },
  {
    "n": 53,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "One Plug To Rule Them",
    "subtitle": "Intuition: a universal connector called MCP is USB-C for AI. One plug, any tool, any AI.",
    "bullets": [
      {
        "head": "The analogy's two sides",
        "sub": "Custom wiring is a drawer of mismatched chargers; MCP is one USB-C that fits everything."
      },
      {
        "head": "What it actually is",
        "sub": "A shared standard for how any AI and any tool talk to each other."
      },
      {
        "head": "Why it changes the math",
        "sub": "Build the plug once and every AI can use that tool, not just one."
      },
      {
        "head": "Who's behind it",
        "sub": "Anthropic introduced it for Claude, and the whole industry is adopting it."
      }
    ],
    "takeaway": "MCP is the universal plug that lets any AI use any tool without custom wiring.",
    "narration": "The fix is beautifully simple. A universal connector, called MCP. Think of it as USB-C for AI. Custom wiring was a drawer full of mismatched chargers that never fit. MCP is the one plug that fits everything. Build the connection once, and any AI can use that tool, not just one. Anthropic introduced it for Claude, and now the whole industry is plugging in.",
    "visualNote": "A messy charger drawer dissolves into a single USB-C plug snapping into many devices."
  },
  {
    "n": 54,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "My Project Is A Plug",
    "subtitle": "EAKC isn't just an AI with tools. It's a tool that other AIs, like Claude, can plug into.",
    "bullets": [
      {
        "head": "What EAKC offers",
        "sub": "It reads your code and documents and answers questions straight from them."
      },
      {
        "head": "The standard plug",
        "sub": "It exposes that knowledge through the same universal connector, so Claude can reach it."
      },
      {
        "head": "What that unlocks",
        "sub": "Ask Claude a question and it pulls live answers from my knowledge base."
      },
      {
        "head": "Why this is the pattern",
        "sub": "The same building block frontier labs ship, built solo by one person."
      }
    ],
    "takeaway": "EAKC works both ways: a chat app, and a live plug other AIs connect into.",
    "narration": "Here's where my own project fits in. EAKC reads your code and documents and answers questions straight from them. But it doesn't just have tools, it is one. It exposes its knowledge through that same universal connector, so Claude can plug straight into it. You ask Claude a question, and it pulls a live answer out of my knowledge base. One solo builder, the very same building block the big labs are shipping.",
    "visualNote": "An EAKC node lights up as a plug; Claude's icon connects and a live answer streams back."
  },
  {
    "n": 55,
    "kicker": "Giving AI Tools",
    "layout": "concept",
    "title": "The New Standard Spreads",
    "subtitle": "This universal connector is quietly becoming the wiring of the entire AI era.",
    "bullets": [
      {
        "head": "Why it caught on",
        "sub": "Build a tool once and it instantly works with every AI that speaks the standard."
      },
      {
        "head": "How fast it's moving",
        "sub": "Big labs and countless tool makers adopted it in a matter of months."
      },
      {
        "head": "What it means for you",
        "sub": "The assistants you use will reach more of your apps with no extra setup."
      },
      {
        "head": "The watch-out",
        "sub": "A shared plug is only as safe as the permissions you give each tool."
      }
    ],
    "takeaway": "A single shared standard is becoming the invisible wiring connecting AI to everything.",
    "narration": "And this standard is spreading fast. The reason is obvious: build a tool once, and it instantly works with every AI that speaks the same language. In just months, the big labs and countless tool makers adopted it. For you, that means your assistants will reach more of your apps with no setup. The one watch-out: a shared plug is only as safe as the permissions you hand each tool.",
    "visualNote": "A single connector replicates outward across a network map, links lighting up like infrastructure."
  },
  {
    "n": 56,
    "kicker": "Giving AI Tools",
    "layout": "statement",
    "title": "Now The Unglamorous Parts",
    "subtitle": "Grounding, learning, testing, tools. Next: the boring work that makes it actually usable.",
    "narration": "So we've given the AI a foundation in your files, the ability to learn, a way to test itself, and now hands to use your software. That's the exciting half. But none of it matters if the thing falls over on a big codebase, loses your data on an error, or quietly burns money. So next, let's talk about the unglamorous engineering that makes all of this actually usable.",
    "visualNote": "Four glowing pillars labeled grounding, learning, testing, tools settle into place; camera pans to a darker, sturdier foundation."
  },
  {
    "n": 57,
    "kicker": "Making It Real",
    "layout": "section",
    "title": "Making It Real",
    "subtitle": "The unglamorous engineering that turns a clever demo into something you can actually trust.",
    "narration": "Here's the part nobody puts in the highlight reel. A flashy AI demo takes a weekend. Something you can rely on every day takes the boring work: knowing when to shut up, keeping costs sane, walling off private data, and not falling over when the job gets big. This is where AI stops being a toy and starts being a tool.",
    "visualNote": "Title slides in over a dim grid that brightens as the words land."
  },
  {
    "n": 58,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Teaching It To Say I Don't Know",
    "subtitle": "When there's no real source, refusing to answer is the right answer.",
    "bullets": [
      {
        "head": "The default is bluffing",
        "sub": "Left alone, AI fills silence with a confident guess that sounds exactly like a fact."
      },
      {
        "head": "No source, no answer",
        "sub": "My project will only answer from your actual files, and stays quiet when it has none."
      },
      {
        "head": "Why it matters most",
        "sub": "A wrong answer you trust is far more dangerous than an honest blank."
      },
      {
        "head": "Refusal is a feature",
        "sub": "The same instinct the big labs are building into ChatGPT and Claude today."
      }
    ],
    "takeaway": "An AI that admits ignorance is more trustworthy than one that always replies.",
    "narration": "The first thing I taught it was humility. Ask any chatbot a question it can't back up, and its instinct is to bluff: a confident, fluent answer that happens to be made up. I'd rather it stop and say I don't know. When there's no real source in your files, silence is the correct response. A wrong answer you believe is worse than no answer at all.",
    "visualNote": "A bluffed answer fades out; a clean I don't know slides in with a checkmark."
  },
  {
    "n": 59,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Why AI Gets Expensive Fast",
    "subtitle": "It re-reads everything from scratch for every single question.",
    "bullets": [
      {
        "head": "No memory between asks",
        "sub": "Each question makes the model re-read all your background text from the very start."
      },
      {
        "head": "You pay per word read",
        "sub": "Every word the model processes costs money, and the same words get charged again and again."
      },
      {
        "head": "It compounds quietly",
        "sub": "Ten questions over one big document means paying to read that document ten full times."
      },
      {
        "head": "The hidden tax",
        "sub": "Most beginners never see this bill, but it's why serious AI usage adds up shockingly fast."
      }
    ],
    "takeaway": "AI cost balloons because it re-reads the same context for every question you ask.",
    "narration": "Here's a surprise about cost. Every time you ask an AI a question, it re-reads all the background you gave it from scratch. There's no memory between questions. You pay for every word it processes, so the same long document gets read and charged again, and again, and again. Ask ten questions and you've paid to read that document ten full times. It adds up shockingly fast.",
    "visualNote": "A stack of pages re-scans top to bottom on each question, a counter ticking up."
  },
  {
    "n": 60,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Caching: Don't Re-Read The Book",
    "subtitle": "Intuition: remember the parts that never change instead of rereading them.",
    "bullets": [
      {
        "head": "Re-read vs remember",
        "sub": "Intuition: rereading the whole book per question versus keeping a bookmark on the unchanged parts."
      },
      {
        "head": "What caching does",
        "sub": "The fixed background is stored once, so only your new question gets fresh processing."
      },
      {
        "head": "Real result for me",
        "sub": "On supported models this cut my running costs well over half, fifty to seventy percent."
      },
      {
        "head": "The catch",
        "sub": "It only helps the repeated parts, and not every model supports remembering them yet."
      }
    ],
    "takeaway": "Cache the unchanging context once and you stop paying to reread it every time.",
    "narration": "The fix is called caching, and the intuition is simple. Don't re-read the entire book for every question. Remember the parts that never change, and only read the new question. The background gets stored once, your fresh question gets processed, and you skip paying for everything you already paid for. For me that cut costs well over half. It's the exact trick the big labs now offer too.",
    "visualNote": "Book stays bookmarked and dim; only a small new question lights up and processes."
  },
  {
    "n": 61,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Each User In Their Own Room",
    "subtitle": "Many customers share the app, but never share a scrap of data.",
    "bullets": [
      {
        "head": "Walled rooms",
        "sub": "Every customer's files and answers live in a sealed space the others can't reach."
      },
      {
        "head": "Why it's non-negotiable",
        "sub": "One leak between customers would expose private code and documents, and end the trust instantly."
      },
      {
        "head": "Built in, not bolted on",
        "sub": "The separation is enforced at the data layer, so a stray question can't cross the wall."
      },
      {
        "head": "Same model, your data",
        "sub": "Everyone uses one shared brain, but it only ever sees the room it's standing in."
      }
    ],
    "takeaway": "Shared software, separated data: every customer's knowledge stays in its own walled room.",
    "narration": "Privacy is the next non-negotiable. The app serves many customers at once, but each one's data lives in its own walled room and never mixes with anyone else's. They share the same underlying brain, but that brain only ever sees the room it's standing in. One leak between customers would expose private code and destroy all trust, so the wall is built into the foundation, not bolted on later.",
    "visualNote": "Three sealed rooms light up independently; a probe bounces off each wall."
  },
  {
    "n": 62,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "A Window Into Its Mind",
    "subtitle": "A dashboard that shows whether the AI is actually healthy.",
    "bullets": [
      {
        "head": "What it is",
        "sub": "A live view of what the AI is learning and how well it's currently doing its job."
      },
      {
        "head": "Why you need it",
        "sub": "An autonomous system you can't see into is a system you can't trust."
      },
      {
        "head": "From guessing to knowing",
        "sub": "Instead of hoping it behaves, I can watch its health like a car dashboard."
      },
      {
        "head": "Catches drift early",
        "sub": "When quality slips, the window shows it before users ever feel the problem."
      }
    ],
    "takeaway": "You can only trust an autonomous AI if you can see inside it while it runs.",
    "narration": "Once an AI starts doing things on its own, you need to see inside it. So I built a dashboard, a window into the AI's mind. It shows what the AI is learning and whether it's healthy right now. That matters because a system that acts on its own, with no way to look in, is a system you're just hoping behaves. With the window, I'm watching, not guessing.",
    "visualNote": "Dark panel boots up into a calm dashboard of live health readouts."
  },
  {
    "n": 63,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Watch What It Refused To Send",
    "subtitle": "Trust comes from seeing its judgment, not just its final output.",
    "bullets": [
      {
        "head": "Output isn't enough",
        "sub": "Seeing only what it sent tells you nothing about the risky messages it almost sent."
      },
      {
        "head": "It blocks its own mistakes",
        "sub": "When a message looks unsafe or wrong, the AI stops itself before it goes out."
      },
      {
        "head": "The window shows the saves",
        "sub": "I can watch every message it refused, so its judgment becomes visible, not hidden."
      },
      {
        "head": "How you trust autonomy",
        "sub": "Seeing good decisions under pressure is what earns trust in a system that acts alone."
      }
    ],
    "takeaway": "An AI you can trust shows you the bad moves it stopped, not just the good ones it made.",
    "narration": "Here's the part I'm proudest of. The dashboard doesn't just show what the AI said. It shows what it refused to send. When a message looks risky or wrong, the AI blocks itself, and I can watch every one of those saves. That's how you actually trust something autonomous: not by admiring its output, but by watching its judgment when nobody's forcing its hand.",
    "visualNote": "A risky message slides toward send, then snaps back behind a glowing stop line."
  },
  {
    "n": 64,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Not Choking On Real Data",
    "subtitle": "Real codebases are huge and messy, and that's most of the work.",
    "bullets": [
      {
        "head": "Demos use tidy data",
        "sub": "A demo runs on a handful of clean files; real projects are giant and chaotic."
      },
      {
        "head": "Big inputs break things",
        "sub": "Feed an AI thousands of messy files and it can stall, slow down, or fall over."
      },
      {
        "head": "Recover, don't lose data",
        "sub": "When something errors mid-job, it now picks up cleanly instead of losing your work."
      },
      {
        "head": "The unseen majority",
        "sub": "A surprising share of the effort is just surviving the size and mess of reality."
      }
    ],
    "takeaway": "Most real AI work is keeping it from choking on huge, messy, real-world data.",
    "narration": "Reliability sounds boring until you hit real data. Demos run on a few tidy files. Real codebases are enormous and messy, and a surprising amount of the work is just keeping the AI from choking on a giant pile of them. It has to stay standing when the input is huge, and when something goes wrong mid-job, recover cleanly instead of losing your work. Unglamorous, and absolutely essential.",
    "visualNote": "A neat file stack swells into a chaotic pile; a steady bar holds firm beneath it."
  },
  {
    "n": 65,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "A 55 Dollar War Story",
    "subtitle": "A runaway loop quietly burned cash for five days before I caught it.",
    "bullets": [
      {
        "head": "What happened",
        "sub": "An automated task got stuck repeating itself and kept calling the AI on its own."
      },
      {
        "head": "The quiet cost",
        "sub": "It silently burned fifty-five dollars over five days before I even noticed."
      },
      {
        "head": "The fix",
        "sub": "Guardrails now cap runaway loops so a stuck task can't bleed money unwatched."
      },
      {
        "head": "Why I'm telling you",
        "sub": "Honest beats polished; autonomous systems fail in expensive, quiet ways you must plan for."
      }
    ],
    "takeaway": "Autonomous AI can fail silently and expensively, so guardrails on cost aren't optional.",
    "narration": "Let me be honest, not polished. One of my automated tasks got stuck in a loop and kept calling the AI over and over, on its own. It quietly burned fifty-five dollars over five days before I noticed. That's the real risk with autonomy: it fails silently and expensively. I added guardrails so a runaway loop gets capped now. I'm telling you because that's the part demos never show.",
    "visualNote": "A cost meter spins up unnoticed, then a guardrail clamps it to a hard stop."
  },
  {
    "n": 66,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "The Boring Part Is The Point",
    "subtitle": "None of this is glamorous, and all of it is what makes AI dependable.",
    "bullets": [
      {
        "head": "Demo vs dependable",
        "sub": "Saying I don't know, caching, walls, guardrails: none of it photographs well."
      },
      {
        "head": "Where demos fail",
        "sub": "Flashy systems break the moment real users, real data, and real money show up."
      },
      {
        "head": "Reliability is the product",
        "sub": "The unsexy work is exactly the difference between a toy and a tool you rely on."
      },
      {
        "head": "The labs do it too",
        "sub": "Behind every polished AI launch sits this same invisible reliability engineering."
      }
    ],
    "takeaway": "The unglamorous work is the whole gap between a flashy demo and a reliable tool.",
    "narration": "Step back and notice something. Saying I don't know, caching context, walling off data, capping runaway costs, surviving messy files: none of it is glamorous. None of it makes a good screenshot. But all of it is the difference between a flashy demo and something you can actually rely on. The big labs do this same invisible work behind every polished launch. Reliability isn't a footnote. It's the product.",
    "visualNote": "Glossy demo card flips to reveal the plain scaffolding holding it up."
  },
  {
    "n": 67,
    "kicker": "Making It Real",
    "layout": "concept",
    "title": "Watch The Brakes, Not The Engine",
    "subtitle": "Intuition: the engine gets the attention, but the safety parts let you drive daily.",
    "bullets": [
      {
        "head": "Engine vs the rest",
        "sub": "Intuition: everyone admires the engine, but brakes, seatbelts, and the dashboard make a car usable."
      },
      {
        "head": "AI's engine is the model",
        "sub": "The clever model is the engine; it's the part everyone points at and gets excited about."
      },
      {
        "head": "The safety parts",
        "sub": "Saying I don't know, cost limits, and the window in are AI's brakes and dashboard."
      },
      {
        "head": "Daily-driveable",
        "sub": "Without those, you've got a race engine on a stand, not a car you can trust."
      }
    ],
    "takeaway": "The model is the engine, but the safety and visibility parts are what let you drive it.",
    "narration": "Here's the intuition that ties it together. Think of a car. Everyone gets excited about the engine, the horsepower, the raw power under the hood. But it's the brakes, the seatbelts, and the dashboard that let you actually drive it every day. The AI model is the engine. Everything I just walked through, the honesty, the cost limits, the window in, those are the brakes and the dashboard.",
    "visualNote": "Spotlight on a glowing engine, then widens to reveal brakes, belts, and dashboard."
  },
  {
    "n": 68,
    "kicker": "Making It Real",
    "layout": "statement",
    "title": "One More Surprise",
    "subtitle": "How this whole thing got built at all might be the most surprising part.",
    "narration": "So that's how you make AI real: the brakes, not just the engine. But there's one more surprise I've been saving, and it's not about a feature at all. It's about how this entire thing got built in the first place, by whom, and in how little time. That story, honestly, changed how I think about what one person can do now. Let me show you.",
    "visualNote": "Everything dims to a single line of text, then a forward arrow pulses to the next section."
  },
  {
    "n": 69,
    "kicker": "Built With AI",
    "title": "Built By One And A Swarm",
    "subtitle": "One person, directing many AIs that wrote the code.",
    "layout": "section",
    "narration": "Here's where it gets strange. Everything you've seen so far - the learning, the dashboard, the self-checks - was built by one person in two weeks. Not because that person is superhuman, but because they were never coding alone. They were directing a swarm of AI agents. This section is about that shift, and why it might be the biggest change of all.",
    "visualNote": "Single figure center, then dozens of small agent dots fan out around them and start moving."
  },
  {
    "n": 70,
    "kicker": "Built With AI",
    "title": "Not Normal Human Speed",
    "subtitle": "Two weeks of work that used to take a team months.",
    "layout": "concept",
    "narration": "Two hundred and twenty-four code changes in two weeks. One person, working a side project around a day job. For context, that's the kind of output you'd normally expect from a small team over a couple of months. So either this person never sleeps, or something about how software gets built just fundamentally changed. It's the second one - and it's worth understanding why.",
    "bullets": [
      {
        "head": "The raw number",
        "sub": "224 separate code changes shipped in fourteen days, by a single solo builder."
      },
      {
        "head": "What that used to mean",
        "sub": "Output on this scale normally needs a small team working for months, not one person."
      },
      {
        "head": "Why it matters",
        "sub": "When one person moves this fast, the limit on building stops being headcount."
      },
      {
        "head": "The honest catch",
        "sub": "Speed alone proves nothing - the real question is how the work held together."
      }
    ],
    "takeaway": "Solo output at team scale is the signal that something changed.",
    "visualNote": "A counter ticks up to 224 fast, then a calendar shrinks from months to two weeks."
  },
  {
    "n": 71,
    "kicker": "Built With AI",
    "title": "I Directed A Team Of Agents",
    "subtitle": "AI helpers wrote, reviewed, and shipped the code alongside me.",
    "layout": "concept",
    "narration": "Here's the actual answer. I didn't type all that code myself. I directed a team of AI coding agents - the same kind of model behind ChatGPT or Claude, but pointed at real work. They wrote code, checked each other's work, and shipped it, while I set the direction and made the calls. Think less lone programmer, more team lead whose whole team happens to be AI.",
    "bullets": [
      {
        "head": "What an agent is",
        "sub": "An AI given a real task and the tools to do it, not just a chat reply."
      },
      {
        "head": "What they actually did",
        "sub": "Wrote code, reviewed each other, and shipped finished work in parallel."
      },
      {
        "head": "My job changed",
        "sub": "From writing every line to setting direction and approving the results."
      },
      {
        "head": "Same models you know",
        "sub": "These are the engines behind ChatGPT and Claude, aimed at building software."
      }
    ],
    "takeaway": "The work got done by a directed team of AIs, not one pair of hands.",
    "visualNote": "One conductor figure; arrows flow out to agents writing code, then results flow back to a checkmark."
  },
  {
    "n": 72,
    "kicker": "Built With AI",
    "title": "Conductor, Not Soloist",
    "subtitle": "Intuition: one person conducting an orchestra of AIs.",
    "layout": "concept",
    "narration": "Here's the intuition. A solo musician can only play one instrument at a time. A conductor doesn't play anything - they direct a whole orchestra, and the music that comes out is bigger than any one player could make. That's the new leverage. You stop being the person doing every task by hand, and become the person pointing many capable workers at the right thing.",
    "bullets": [
      {
        "head": "The analogy",
        "sub": "Soloist plays one instrument; conductor directs a hundred and makes a symphony."
      },
      {
        "head": "What changed for the builder",
        "sub": "You stop playing every part yourself and start directing many at once."
      },
      {
        "head": "Where the skill moves",
        "sub": "Value shifts from typing fast to knowing what to ask for and what's good."
      },
      {
        "head": "The watch-out",
        "sub": "A conductor still has to hear wrong notes - bad direction multiplies into bad output."
      }
    ],
    "takeaway": "The new leverage is conducting many AIs, not playing every instrument yourself.",
    "visualNote": "Single player with one instrument morphs into a conductor before a full orchestra of agents."
  },
  {
    "n": 73,
    "kicker": "Built With AI",
    "title": "Who Gets To Build Now",
    "subtitle": "The real headline isn't a feature - it's who can build, and how fast.",
    "layout": "concept",
    "narration": "So here's the headline, bigger than any single feature. The thing that used to need a funded team and months of time was done by one motivated person in two weeks. That's not just a faster way to code. It changes who gets to build at all. An idea in someone's head can now become a working product without waiting for a company, a budget, or a crew. That's the era we're in.",
    "bullets": [
      {
        "head": "The bigger story",
        "sub": "Not a clever feature - a shift in who is even able to build software."
      },
      {
        "head": "The barrier that fell",
        "sub": "Big builds once needed a funded team; now one directed person can reach far."
      },
      {
        "head": "Why it matters to you",
        "sub": "Ideas can become working tools without waiting on a company or a budget."
      },
      {
        "head": "The honest edge",
        "sub": "Faster building also means more rough, untested work - direction and judgment still decide quality."
      }
    ],
    "takeaway": "The headline of this era is who gets to build, and how fast.",
    "visualNote": "Lone figure points at a glowing idea; it expands into a finished product as the orchestra plays behind."
  },
  {
    "n": 74,
    "kicker": "The Convergence",
    "layout": "section",
    "title": "The Same Building Blocks, Found Twice",
    "subtitle": "One solo builder and a frontier lab quietly reached for the exact same ideas.",
    "narration": "Here's the twist that makes the whole story land. One person, building alone for two weeks, kept reaching for the same handful of ideas the biggest AI labs are shipping right now for tools like Claude and ChatGPT. Nobody copied anyone. They just hit the same problems and arrived at the same answers. Let's look at those building blocks side by side.",
    "visualNote": "Two paths slide in from left and right, meeting at a shared seam in the center."
  },
  {
    "n": 75,
    "kicker": "The Convergence",
    "layout": "compare",
    "title": "Six Ideas, Two Builders",
    "subtitle": "The same modern-AI building blocks, in plain words, on both sides.",
    "compareRows": [
      {
        "eakc": "A private knowledge base that answers from your own files and links the exact source",
        "claude": "Claude answering from the documents you give it, with citations"
      },
      {
        "eakc": "Memory plus lessons it writes for itself after each task",
        "claude": "Skills and memory that let the assistant carry know-how forward"
      },
      {
        "eakc": "One universal connector so other AI tools can plug in",
        "claude": "MCP, the shared socket that lets assistants talk to your tools"
      },
      {
        "eakc": "Automatic exams that catch a confident but out-of-date answer",
        "claude": "The labs' evals that score quality before anything ships"
      },
      {
        "eakc": "Goal-driven helpers that carry out a multi-step task",
        "claude": "Agents that pursue a goal instead of answering one question"
      },
      {
        "eakc": "Reusing repeated context instead of re-sending it, to cut cost",
        "claude": "Prompt caching, which charges less for context it has seen before"
      }
    ],
    "takeaway": "The same six shapes show up whether you're alone or a billion-dollar lab.",
    "narration": "Look at these side by side. A private knowledge base with sources. Memory and self-written lessons. A universal connector so tools plug in. Automatic exams for quality. Goal-driven agents. And reusing context to save money. On the left, what one builder made. On the right, what the big labs ship for Claude. Same six ideas, different scale, almost word for word.",
    "visualNote": "Rows light up in pairs left-then-right, a thin line linking each matched pair."
  },
  {
    "n": 76,
    "kicker": "The Convergence",
    "layout": "concept",
    "title": "Not A Coincidence",
    "subtitle": "A solo developer and a multi-billion-dollar lab reached for the same ideas independently.",
    "bullets": [
      {
        "head": "Same answers, no copying",
        "sub": "Neither side cribbed from the other; both landed on the identical handful of moves."
      },
      {
        "head": "Scale didn't change the shape",
        "sub": "One person in two weeks and a huge lab still chose the same fundamental building blocks."
      },
      {
        "head": "Why it matters to you",
        "sub": "When independent builders agree, you're looking at real structure, not hype or fashion."
      },
      {
        "head": "The honest caveat",
        "sub": "The solo version is rougher and smaller, but the underlying ideas are genuinely the same."
      }
    ],
    "takeaway": "When people working apart land on the same design, that design is real.",
    "narration": "Think about what convergence actually means. Two builders who never compared notes ended up with the same toolkit. That's the opposite of a fad. When independent people, at wildly different scales, keep choosing the same moves, it's a sign those moves are the real structure of this technology, not marketing. The solo version is rougher, sure, but the bones are identical.",
    "visualNote": "Two separate sketches fade up, then snap into alignment as a shared blueprint."
  },
  {
    "n": 77,
    "kicker": "The Convergence",
    "layout": "concept",
    "title": "Same Problems, Same Shapes",
    "subtitle": "Everyone building with AI hits the same four walls and answers them the same way.",
    "bullets": [
      {
        "head": "Trust",
        "sub": "Can I believe the answer? You ground it in real sources and test it with exams."
      },
      {
        "head": "Memory",
        "sub": "Does it remember and improve? You add lessons and skills it carries forward."
      },
      {
        "head": "Cost",
        "sub": "Will it bankrupt me? You reuse context and add guardrails after a runaway burns money."
      },
      {
        "head": "Control",
        "sub": "Can I see and undo what it does? You make its choices visible and reversible."
      }
    ],
    "takeaway": "Trust, memory, cost, and control are the four forces shaping every AI product.",
    "narration": "Here's why the convergence happens. Everyone building with AI runs into the same four walls. Can I trust the answer? Will it remember and get better? Will the cost spiral? And can I see and undo what it does? Face those four problems and you naturally invent the same shapes to solve them. That's the engine underneath the whole pattern.",
    "visualNote": "Four labeled forces press inward on a center block, which settles into a stable shape."
  },
  {
    "n": 78,
    "kicker": "The Convergence",
    "layout": "concept",
    "title": "Your AI Literacy Starts Here",
    "subtitle": "Learn these six ideas once and you'll spot them in every AI product you touch.",
    "bullets": [
      {
        "head": "Six ideas, not a hundred",
        "sub": "Knowledge with sources, memory, connectors, evals, agents, and caching cover most of it."
      },
      {
        "head": "They repeat everywhere",
        "sub": "The same shapes power Claude, ChatGPT, and the next app you try this year."
      },
      {
        "head": "You can name them now",
        "sub": "You could explain each one to a friend, which is what real literacy means."
      },
      {
        "head": "What to watch next",
        "sub": "Notice which idea a new tool is selling; that tells you what it's really for."
      }
    ],
    "takeaway": "These six ideas are the literacy of this AI era, recognizable everywhere.",
    "narration": "So if you're new to AI, you don't need to learn a thousand buzzwords. You need these six ideas: knowledge with sources, memory, a universal connector, automatic exams, agents, and reusing context to save money. Learn them once and you'll recognize them in Claude, in ChatGPT, in whatever launches next. That's not trivia. That's the literacy of this entire era.",
    "visualNote": "Six glowing tokens lock into a row, then echo faintly across distant product frames."
  },
  {
    "n": 79,
    "kicker": "Close",
    "title": "The Honest Scorecard",
    "layout": "concept",
    "subtitle": "What works today, what is still rough, and why showing the rough edges is the point.",
    "bullets": [
      {
        "head": "Solid today",
        "sub": "It answers from your own files with links to the exact source you can check yourself."
      },
      {
        "head": "Learning, the basics",
        "sub": "After a task it writes itself a short lesson and reuses it next time."
      },
      {
        "head": "Still rough",
        "sub": "Making that learning fully hands-off, plus a couple of safety nets, are unfinished."
      },
      {
        "head": "Real cost cuts",
        "sub": "Reusing repeated context instead of resending it cut costs fifty to seventy percent."
      }
    ],
    "takeaway": "Honest software names its unfinished edges instead of hiding them behind a demo.",
    "narration": "So where does this actually stand? Answering from your own documents with real sources works today. The basics of learning work, the dashboard works, and the cost cuts are real. What is still rough? Making the learning fully automatic, a couple of safety nets, and a few features still behind switches. And honestly, showing you those unfinished edges is the whole point.",
    "visualNote": "Two columns slide in, green checks left, amber dots right, balanced."
  },
  {
    "n": 80,
    "kicker": "Close",
    "title": "Come Along For The Middle",
    "layout": "closing",
    "subtitle": "Built in private for fun, now sharing the messy, exciting middle. If that sounds interesting, follow along.",
    "narration": "Here is the thing. The same building blocks the big labs are shipping for ChatGPT and Claude, answering from your files, memory, tool connectors, caching, automatic exams, I reached for them too, as one person. Most projects only get shown once they are polished. I would rather share the messy, exciting middle while it is still being figured out. If that is interesting, follow along. And thank you for watching.",
    "visualNote": "Title settles, soft glow, gentle fade to channel handle."
  }
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
